import {AppState} from './../../../store';
import {createSelector} from 'reselect';
import {
  hotelIdSelectorFnFromCommon,
  checkinSelectorFn,
  checkoutSelectorFn,
  roomStringSelectorFn,
} from '@stencil/hotels-common-reducer/src/selectors';
import {DetailStaticShape} from '../redux/types';

export const detailSelector = (state: AppState) => state.details;
export const hermesDataSelector = createSelector(detailSelector, details => details?.hermesData);

// DETAILS STATIC

export const locationMapImageSelectorFn = (state: AppState) =>
  state.details?.staticData?.map_marker?.desktop;
export const latLongSelectorFn = (state: AppState) => {
  const la = state.details?.staticData?.la;
  const lo = state.details?.staticData?.lo;

  if (la && lo) {
    return {
      la,
      lo,
    };
  }

  return {};
};

export const pointOfInterestsSelectorFn = (state: AppState) =>
  state.details?.staticData?.poi_data || [];

export const pointOfInterestsDataSelector = createSelector(
  [pointOfInterestsSelectorFn],
  pointOfInterests => pointOfInterests,
);

export const cityId = (state: AppState) => state.details?.staticData?.vcid;
export const cityIdSelector = createSelector(cityId, cityId => cityId);

export const allStaticDataSelector = createSelector(
  detailSelector,
  details => details.staticData || ({} as DetailStaticShape),
);

export const countryNameSelector = createSelector(
  allStaticDataSelector,
  allStaticData => allStaticData?.cn || 'Country',
);
export const seoMarkupDataSelector = createSelector(
  allStaticDataSelector,
  allStaticData => allStaticData?.seo_markup_data || ({} as DetailStaticShape['seo_markup_data']),
);

export const hotelCityURLSelectorFn = createSelector(
  seoMarkupDataSelector,
  seoMarkupData => seoMarkupData?.hotel_city_url,
);
export const countryUrlSelector = createSelector(
  seoMarkupDataSelector,
  seoMarkupData => seoMarkupData?.country_url,
);

export const hotelCityURLSelector = createSelector(
  hotelCityURLSelectorFn,
  hotelCityURL => hotelCityURL,
);

export const goStaysSelector = (state: AppState) => state.details?.staticData?.ty === 'gostays';
export const hotelRatingSelector = (state: AppState) => state.details?.staticData?.hr;
export const hotelName = (state: AppState) => state.details?.staticData?.hn;
export const hotelAddressSelector = (state: AppState) => state.details?.staticData?.address;

export const hotelArea = (state: AppState) => state.details?.staticData?.c;
export const cityNameSelector = createSelector(hotelArea, cityName => cityName);

export const hotelCountrySelector = (state: AppState) => state.details?.staticData?.cn;
export const hotelAreaPincodeSelector = (state: AppState) => state.details?.staticData?.pincode;
export const hotelStaticRatingSelector = (state: AppState) => state.details?.staticData?.gr;
export const hotelStaticReviewsNumberSelector = (state: AppState) =>
  state.details?.staticData?.gir_rcnt;
export const hotelRatingSourceSelector = (state: AppState) => state.details?.staticData?.gri;
export const hotelStaticMinPrice = (state: AppState) => state.details?.staticData?.minprice;
export const ugcSentiments = (state: AppState) => state.details?.staticData?.ugc_sentiments;
// 🐨 profile – this selector will return a new reference everytime. Remember to use shallow equals
//whenever this selector is used
export const indexedRoomsStaticDataSelectorFn = (state: AppState) => {
  const roomInfo = state.details?.staticData?.room_info || [];

  type IndexedRoomInfoType = {
    [key: string]: typeof roomInfo[0];
  };

  let indexedRoomInfo: IndexedRoomInfoType = {};

  roomInfo.forEach(room => {
    if (room?.type_code) {
      indexedRoomInfo[room.type_code] = room;
    }

    if (room.type_codes && Array.isArray(room.type_codes)) {
      room.type_codes.forEach(code => {
        indexedRoomInfo[code] = room;
      });
    }
  });

  return indexedRoomInfo;
};

export const indexedRoomsStaticDataSelector = createSelector(
  indexedRoomsStaticDataSelectorFn,
  indexedRoomsStaticData => indexedRoomsStaticData,
);

export const hotelierGallery = (state: AppState) => state.details?.staticData?.gallery || [];
export const travellerGallery = (state: AppState) => state.details?.staticData?.ugc_images || [];
export const captivateGallery = (state: AppState) => state.details?.staticData?.gallery_v2 || [];
export const totalTravellerImages = (state: AppState) =>
  state.details.staticData?.gir_data?.approved_img_cnt || 0;
// export const hotelAmenities = (state: AppState) =>
//   (
//     state.details.staticData.facilities
//     || {
//       all: [],
//       cmapped: [],
//       categorized: []
//     } as DetailStaticShape["facilities"]) as Required<DetailStaticShape, 'facilities'>

export const hotelAmenitiesSelectorFn = (state: AppState) =>
  state.details?.staticData?.facilities || {
    all: [],
    cmapped: [],
    categorized: [],
  };

export const hotelsAmenitiesSelector = createSelector(
  hotelAmenitiesSelectorFn,
  hotelAmenities => hotelAmenities,
);

// type Id<T> = {} & { [P in keyof T]: T[P] }
// type A = Required<DetailStaticShape, 'facilities'>

// const a: A = {
//   fa
// }
export const roomsStaticData = (state: AppState) => state.details.staticData.room_info || [];

// HERMES DATA
export const hasHermesDataBooleanSelectorFn = (state: AppState) => state.details.hasHermesData;
export const hasHermesDataBooleanSelector = createSelector(
  hasHermesDataBooleanSelectorFn,
  hasHermesData => hasHermesData,
);

export const metaInfoSelector = createSelector(
  hermesDataSelector,
  hermesData => hermesData.meta_info || {},
);

export const metaFlagsSelector = createSelector(
  metaInfoSelector,
  metaInfo => metaInfo?.flags || {},
);
export const showPaxSplittedRoomStringSelector = createSelector(
  metaFlagsSelector,
  flags => flags?.pxgrn,
);
export const paxSplittedRoomStringSelector = createSelector(
  metaInfoSelector,
  metaInfo => metaInfo?.altpx,
);

export const paxSplittedDataSelector = createSelector(
  [showPaxSplittedRoomStringSelector, paxSplittedRoomStringSelector],
  (showPaxSplittedRoomString, paxSplittedRoomStringSelector) => {
    if (showPaxSplittedRoomString) {
      return paxSplittedRoomStringSelector;
    }

    return false;
  },
);

export const hotelPersuasionSelector = (state: AppState) => state.details.hermesData.data?.tp || [];
export const isHermesLoading = (state: AppState) => state.details.isHermesDataLoading;
export const roomsDataHermes = (state: AppState) => state.details.hermesData?.data?.reg || [];
export const roomDataHermesSelector = createSelector(
  roomsDataHermes,
  roomsDataHermes => roomsDataHermes,
);

// needed for hotel policy url
export const oldPaxSelector = createSelector(
  roomDataHermesSelector,
  roomsDataHermes => roomsDataHermes[0]?.rpl[0]?.pd?.pax,
);

// export const roomsFilterDataFn= (state: AppState) => state.details.hermesData.meta_info?.rf || [];
export const roomsFilterDataSelector = createSelector(
  metaInfoSelector,
  metaInfo => metaInfo?.rf || [],
);

export const countryCode = (state: AppState) => state.details.hermesData.meta_info?.coc || '';

export const showHotelPolicyFromHermesSelectorFn = (state: AppState) =>
  !!state.details.hermesData.meta_info?.flags?.is_hp;
export const hotelPolicyFromHermesSelectorFn = (state: AppState) =>
  state.details.hermesData.meta_info?.hp;

export const hotelPolicyFromHermesSelector = createSelector(
  [showHotelPolicyFromHermesSelectorFn, hotelPolicyFromHermesSelectorFn],
  (showHotelPolicyFromHermes, hotelPolicyListFromHermes) => ({
    showHotelPolicyFromHermes,
    hotelPolicyListFromHermes,
  }),
);

const similarHotelsSelectorFn = (state: AppState) => state.details.similarHotels;

export const similarHotelsSelector = createSelector(
  similarHotelsSelectorFn,
  similarHotelsData => similarHotelsData,
);

export const coupleFriendlyFlagSelectorFn = (state: AppState) =>
  !!state.details.hermesData.meta_info?.flags?.cf;
export const gostayFlagSelectorFn = (state: AppState) =>
  !!state.details.hermesData.meta_info?.flags?.gostay;
export const gostaysHoverDataSelectorFn = (state: AppState) =>
  state.details.hermesData.meta_info?.gsm || [];
export const coupleFriendlyDataSelectorFn = (state: AppState) =>
  state.details.hermesData.data?.tlp || [];

// 🐨 see how to fix types
export const mixedPersuasions = (state: AppState) => {
  const {per = {}} = state.details.hermesData?.meta_info || {};
  return per;
};

export const detailsStaticDataSelector = createSelector(
  hotelName,
  hotelAddressSelector,
  hotelRatingSelector,
  hotelStaticRatingSelector,
  (detailsHotelName, detailsHotelAddress, detailsHotelStars, detailsRating) => ({
    detailsHotelName,
    detailsHotelAddress,
    detailsHotelStars,
    detailsRating,
  }),
);

export const socialPersuasionDataSelectorFn = (state: AppState) => {
  return state.details.hermesData?.data?.sp;
};

export const socialPersuasionDataSelector = createSelector(
  socialPersuasionDataSelectorFn,
  socialPersuasionData => socialPersuasionData,
);

// export const locationPersuasions = (state: AppState) => {
//   const { ptl = {}, ptk = '' } = state.details.hermesData?.meta_info || {};

//   const dealsAndPersuasions = ptl[ptk] || [];

//   const dealsDistinguisher = ['dealsHotelOfferText', 'spotDeal'];

//   // const deals =
//   //   (dealsAndPersuasions &&
//   //     dealsAndPersuasions.filter(
//   //       persuasionOrDeal =>
//   //         persuasionOrDeal.pid &&
//   //         dealsDistinguisher.includes(persuasionOrDeal.pid)
//   //     )) ||
//   //   [];

export const locationPersuasions = (state: AppState) => state.details.staticData?.usp?.loc || [];
export const freeBreakfastOrCancellationPersuasionAndAdditionalPersuasionSelector = (
  state: AppState,
) => {
  return {
    breakFastOrCancellation: state.details.hermesData?.data?.cp2 || [],
    additionalPersuasion: state.details.hermesData?.data?.cp3 || [],
    dummyPersuasions: state.details.hermesData?.data?.cp1 || [],
  };
};

// UI STATE
export const currentRoomData = (state: AppState) => state.details.UI.currentRoomData;

export const hotelIdSelectorFnFromDetails = (state: AppState) => state.details.vhid || '';
export const offerCodeSelector = createSelector(detailSelector, details => {
  return details?.offerCodeFromQueryParam;
});

export const hotelIdSelector = createSelector(
  [hotelIdSelectorFnFromDetails, hotelIdSelectorFnFromCommon],
  (hotelIdFromDetails, hotelIdFromCommon) => {
    return hotelIdFromDetails || hotelIdFromCommon;
  },
);

export const selectedHotelStatic = (state: AppState) => state.details.selectedHotelStatic || {};
export const hotelNameSelectedHotelStaticSelector = createSelector(
  selectedHotelStatic,
  selectedHotelStatic => selectedHotelStatic?.hn,
);
export const isStaticDataLoading = (state: AppState) => state.details.isStaticDataLoading;

export const hasStaticDataSelector = (state: AppState) => state.details.hasStaticData;

export const hotelPolicyData = (state: AppState) => state.details.hotelPolicyData;
export const hotelPolicyDataSelector = createSelector(
  hotelPolicyData,
  hotelPolicyData => hotelPolicyData,
);

export const hotelPolicyAdditionalDataSelector = createSelector(
  hotelPolicyData,
  hotelPolicyData => {
    const additionalHotelPoliciesDataObj = hotelPolicyData.hotelPolicyAdditionalData || [];

    const flattenedAdditionalHotelPoliciesList = Object.keys(additionalHotelPoliciesDataObj).reduce(
      (acc, nextKey) => {
        const annoucementsForKey = additionalHotelPoliciesDataObj[nextKey] || [];
        return [...acc, ...annoucementsForKey];
      },
      [],
    );

    return flattenedAdditionalHotelPoliciesList;
  },
);

//Checkin and checkout time
export const checkinTime = (state: AppState) => state.details.staticData.check_in || '';
export const checkoutTime = (state: AppState) => state.details.staticData.check_out || '';

//For processed FAQ data
export const processedFaqDataSelector = (state: AppState) =>
  state.details.staticData?.meta_config?.faqs || [];

export const detailHttpCode = (state: AppState) =>
  state.details.staticData?.status?.http_code || 200;

export const detailHttpCodeSelector = createSelector(detailHttpCode, detailHttpCode =>
  detailHttpCode.toString(),
);

export const detailUrlFromStaticData = (state: AppState) =>
  state.details.staticData?.hotel_url || '';

export const detailUrlFromStaticDataSelector = createSelector(
  detailUrlFromStaticData,
  item => item,
);

export const detailSelectorFn = createSelector(detailSelector, item => item);

export const staticDataSelectorFn = createSelector(detailSelector, item => item.staticData);

export const seoDataFromStaticDataFn = createSelector(
  staticDataSelectorFn,
  item => item?.seo_markup_data || {},
);

export const rnrPageNumberSelectorFn = createSelector(detailSelector, item => item.page);

export const appliedFiltersSelector = createSelector(
  detailSelector,
  item => item.appliedFilters || [],
);

//For details page About property
export const aboutProperty = (state: AppState) => state.details.processedAboutPropertyData || '';
export const aboutPropertySelectorFn = createSelector(
  aboutProperty,
  aboutProperty => aboutProperty,
);
// thunk selectors

export const similarHotelsThunkSelector = createSelector(
  [
    checkinSelectorFn,
    checkoutSelectorFn,
    roomStringSelectorFn,
    paxSplittedDataSelector,
    cityIdSelector,
    hotelIdSelector,
  ],
  (checkinObj, checkoutObj, roomstring, paxSplittedRoomString, cityId, hotelCode) => {
    return {
      checkinObj,
      checkoutObj,
      roomstring,
      paxSplittedRoomString,
      cityId,
      hotelCode,
    };
  },
);

export const hotelPolicyThunkSelector = createSelector(
  [
    checkinSelectorFn,
    checkoutSelectorFn,
    roomStringSelectorFn,
    paxSplittedDataSelector,
    cityIdSelector,
    hotelIdSelector,
    roomDataHermesSelector,
    oldPaxSelector,
    hotelPolicyFromHermesSelector,
  ],
  (
    checkinObj,
    checkoutObj,
    roomstring,
    paxSplittedRoomString,
    cityId,
    hotelCode,
    roomsDataHermes,
    oldPax,
    hotelPolicyFromHermesData,
  ) => {
    return {
      checkinObj,
      checkoutObj,
      roomstring,
      paxSplittedRoomString,
      cityId,
      hotelCode,
      roomsDataHermes,
      oldPax,
      hotelPolicyFromHermesData,
    };
  },
);

export const goSafeSegmentDataSelectorFn = createSelector(detailSelector, item => {
  let data = {};
  if (item?.staticData?.segment_block?.length) {
    const {
      cards = [],
      title = '',
      title_color: titleColor = '',
      footer: footerText = '',
      footer_color: footerColor,
      godata = {},
      logo = '',
      persuation_title: persuationTitle = '',
    } = item?.staticData?.segment_block[0];
    data = {cards, title, titleColor, footerText, footerColor, godata, persuationTitle, logo};
  }
  return data;
});

export const goSafePersuasionSelector = createSelector(
  hermesDataSelector,
  item => item.data?.npp || [],
);
